import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import type { ProgrammeType } from 'src/types/Programme'
import { useAuthStore } from 'src/stores/auth-store'

const applicantChildrenRoutes = (prefix = '') => [
  {
    path: '',
    name: prefix + 'Applicant Profile',
    component: () => import('pages/applicant/ApplicantProfile.vue'),
  },
  {
    path: 'update',
    name: prefix + 'Applicant Profile Update',
    component: () => import('pages/applicant/ApplicantProfileUpdate.vue'),
  },
  {
    path: 'contact-infos',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Contact Infos',
        component: () => import('pages/applicant/ApplicantContactInfos.vue'),
      },
      {
        path: 'create',
        name: prefix + 'Contact Info Create',
        component: () => import('src/pages/contact_info/ContactInfoCreate.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Contact Info Update',
        component: () => import('src/pages/contact_info/ContactInfoUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
    ],
  },
  {
    path: 'education-degrees',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Education Degrees',
        component: () => import('pages/applicant/ApplicantEducationDegrees.vue'),
      },
      {
        path: 'create',
        name: prefix + 'Education Degree Create',
        component: () => import('src/pages/education_degree/EducationDegreeCreate.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Education Degree Update',
        component: () => import('src/pages/education_degree/EducationDegreeUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
    ],
  },
  {
    path: 'language-degrees',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Language Degrees',
        component: () => import('pages/applicant/ApplicantLanguageDegrees.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Language Degree Update',
        component: () => import('src/pages/language_degree/LanguageDegreeUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
      {
        path: 'create',
        name: prefix + 'Language Degree Create',
        component: () => import('src/pages/language_degree/LanguageDegreeCreate.vue'),
      },
    ],
  },
  {
    path: 'other-documents',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Other Documents',
        component: () => import('pages/applicant/ApplicantOtherDocuments.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Other Document Update',
        component: () => import('src/pages/other_document/OtherDocumentUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
      {
        path: 'create',
        name: prefix + 'Other Document Create',
        component: () => import('src/pages/other_document/OtherDocumentCreate.vue'),
      },
    ],
  },
  {
    path: 'work-experiences',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Work Experiences',
        component: () => import('pages/applicant/ApplicantWorkExperiences.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Work Experience Update',
        component: () => import('src/pages/work_experience/WorkExperienceUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
      {
        path: 'create',
        name: prefix + 'Work Experience Create',
        component: () => import('src/pages/work_experience/WorkExperienceCreate.vue'),
      },
    ],
  },
  {
    path: 'certifications',
    children: [
      {
        path: '',
        name: prefix + 'Applicant Certifications',
        component: () => import('pages/applicant/ApplicantCertifications.vue'),
      },
      {
        path: ':id(\\d+)/update',
        name: prefix + 'Certification Update',
        component: () => import('src/pages/certification/CertificationUpdate.vue'),
        props: (route: RouteLocationNormalized) => ({
          id: Number(route.params.id),
        }),
      },
      {
        path: 'create',
        name: prefix + 'Certification Create',
        component: () => import('src/pages/certification/CertificationCreate.vue'),
      },
    ],
  },
]

const routes: RouteRecordRaw[] = [
  {
    path: '/auth-callback',
    component: () => import('pages/AuthCallback.vue'),
    name: 'Auth Callback',
  },
  {
    path: '/applications/:id/zip/download',
    name: 'application-zip-download',
    component: () => import('pages/DownloadZipPage.vue'),
    meta: {
      fileDownload: true,
      isAuthenticated: true,
    },
    props: { mode: 'application' },
  },
  {
    path: '/applications/:id/applicant/zip/download',
    name: 'applicant-zip-download',
    component: () => import('pages/DownloadZipPage.vue'),
    meta: {
      fileDownload: true,
      isAuthenticated: true,
    },
    props: { mode: 'profile' },
  },
  {
    path: '/:lang(en|el)',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('pages/IndexPage.vue'),
        name: 'Home',
        meta: {
          hasHero: true,
        },
      },
      {
        path: 'privacy',
        component: () => import('pages/PrivacyPolicyPage.vue'),
        name: 'Privacy',
      },
      {
        path: 'terms',
        component: () => import('pages/TermsPage.vue'),
        name: 'Terms',
      },
      {
        path: 'upload-reference/:token',
        component: () => import('pages/ReferenceUploadForm.vue'),
        name: 'Upload Reference',
        props: (route) => ({
          token: route.params.token,
        }),
      },
      {
        path: 'survey/:token',
        component: () => import('pages/PublicSurveyPage.vue'),
        name: 'Public Survey',
        props: (route) => ({
          token: route.params.token,
        }),
      },
      {
        path: 'about',
        component: () => import('pages/AboutPage.vue'),
        name: 'About',
        meta: {
          hasHero: true,
        },
      },
      {
        path: 'how-to',
        component: () => import('pages/HowToApply.vue'),
        name: 'How To Apply',
        meta: {
          hasHero: true,
        },
      },
      {
        path: 'programmes/:programmeId(\\d+)/calls/:id(\\d+)/details',
        name: 'Internal Call Details',
        component: () => import('pages/call/CallDetails.vue'),
        props: (route) => ({
          id: Number(route.params.id),
          programmeId: Number(route.params.programmeId),
        }),
        meta: { isInternal: true },
      },
      {
        path: 'programmes/:programmeId(\\d+)/calls/external/:id(\\d+)/details',
        name: 'External Call Details',
        component: () => import('pages/call/CallDetails.vue'),
        props: (route) => ({
          id: Number(route.params.id),
          programmeId: Number(route.params.programmeId),
        }),
        meta: { isInternal: false },
      },
      {
        path: 'universities',
        meta: {
          hasHero: true,
        },
        children: [
          {
            path: '',
            component: () => import('pages/university/UniversitiesPage.vue'),
            name: 'Universities',
            props: (route) => ({
              page: Number(route.query.page) || 1,
              name: route.query.name || undefined,
            }),
          },
          {
            path: ':id/details/:slug?',
            name: 'University Details',
            component: () => import('pages/university/UniversityDetails.vue'),

            props: (route) => ({
              id: Number(route.params.id),
              page: Number(route.query.page) || 1,
              limit: Number(route.query.limit) || 10,
            }),
          },
        ],
      },
      {
        path: 'programmes',
        children: [
          {
            path: '',
            component: () => import('pages/programme/ProgrammesPage.vue'),
            name: 'Programmes',
            meta: {
              hasHero: true,
            },
            props: (route) => ({
              name: route.query.name,
              prog_type: route.query.prog_type,
              city: route.query.city,
              university_id: Number(route.query.university_id) || null,
              has_tuition_fees: route.query.has_tuition_fees,
              is_open: route.query.is_open,
              apply_status: route.query.apply_status,
              language: route.query.language,
              topic: route.query.topic,
              study_mode: route.query.study_mode,
              page: Number(route.query.page) || 1,
              limit: Number(route.query.limit) || 10,
            }),
          },
          {
            path: ':type/:id(\\d+)/details/:slug?',
            name: 'Programme Details',
            component: () => import('pages/programme/ProgrammeDetails.vue'),
            props: (route) => ({
              id: Number(route.params.id),
              type: (route.params.type as ProgrammeType) || 'msc',
            }),
          },
        ],
      },
      {
        path: 'applications',
        component: () => import('layouts/ApplicationLayout.vue'),

        meta: {
          isAuthenticated: true,
          profileRequired: true,
          isAgent: true,
          isApplicant: true,
        },
        children: [
          {
            path: '',
            component: () => import('pages/application/ApplicationsPage.vue'),
            name: 'Applications',
            beforeEnter: (to, from, next) => {
              if (!to.query.status) {
                next({ ...to, query: { ...to.query, status: 'Finalized' }, replace: true })
              } else {
                next()
              }
            },
            props: (route) => {
              const authStore = useAuthStore()
              return {
                type: route.query.type || undefined,
                status: route.query.status || 'Finalized',
                mode: authStore.isApplicant ? 'applicant-applications' : 'agent-applications',
              }
            },
          },
          {
            path: '',
            component: () => import('layouts/ApplicationViewLayout.vue'),
            children: [
              {
                path: ':id(\\d+)/create',
                component: () => import('pages/application/ApplicationCreate.vue'),
                name: 'Application Create',
                props: (route) => ({
                  id: Number(route.params.id),
                }),
              },
              {
                path: ':id(\\d+)/update',
                name: 'Application Update',
                component: () => import('pages/application/ApplicationUpdate.vue'),
                props: (route) => ({
                  id: Number(route.params.id),
                  openMessages: route.query.openMessages === 'true',
                }),
              },
              {
                path: ':id(\\d+)/details',
                name: 'Application Details',
                component: () => import('pages/application/ApplicationDetails.vue'),
                props: (route) => ({
                  id: Number(route.params.id),
                  openMessages: route.query.openMessages === 'true',
                }),
              },
            ],
          },
        ],
      },
      {
        path: 'applicant-create',
        component: () => import('layouts/applicant/ProfileCreateLayout.vue'),
        meta: {
          isAuthenticated: true,
          isApplicant: true,
        },
        children: [
          {
            path: '',
            name: 'Applicant Create',
            component: () => import('pages/applicant/ApplicantCreate.vue'),
            props: (route) => ({ next: route.query.next }),
          },
        ],
      },
      {
        path: 'agent',
        meta: { isAuthenticated: true, isAgent: true },

        children: [
          {
            path: 'my-agents',
            name: 'Agent Agents',
            component: () => import('pages/agent/AgentList.vue'),
          },
          {
            path: 'applicants',
            name: 'Agent Applicants',
            component: () => import('layouts/agent/ApplicantsLayout.vue'),
            props: (route) => ({ next: route.query.next }),

            children: [
              {
                path: 'create',
                name: 'Agent Applicant Create',
                component: () => import('pages/applicant/ApplicantCreate.vue'),
              },
              {
                path: ':uuid/profile',
                component: () => import('src/layouts/agent/ApplicantProfileLayout.vue'),
                meta: { actingAsRequired: true },

                children: [
                  ...applicantChildrenRoutes('Agent '),

                  {
                    path: 'applications',
                    children: [
                      {
                        path: '',
                        component: () => import('pages/application/ApplicationsPage.vue'),
                        name: 'Agent Applicant Applications',
                        props: (route) => ({
                          type: route.query.type || undefined,
                          status: route.query.status || 'Finalized',
                          mode: 'agent-applicant-applications',
                        }),
                        beforeEnter: (to, from, next) => {
                          if (!to.query.status) {
                            next({
                              ...to,
                              query: { ...to.query, status: 'Finalized' },
                              replace: true,
                            })
                          } else {
                            next()
                          }
                        },
                      },
                      {
                        path: '',
                        component: () =>
                          import('layouts/agent/ApplicantProfileApplicationViewLayout.vue'),
                        children: [
                          {
                            path: ':id(\\d+)/create',
                            component: () => import('pages/application/ApplicationCreate.vue'),
                            name: 'Agent Applicant Application Create',
                            props: (route) => ({
                              id: Number(route.params.id),
                            }),
                          },
                          {
                            path: ':id(\\d+)/update',
                            name: 'Agent Applicant Application Update',
                            component: () => import('pages/application/ApplicationUpdate.vue'),
                            props: (route) => ({
                              id: Number(route.params.id),
                            }),
                          },
                          {
                            path: ':id(\\d+)/details',
                            name: 'Agent Applicant Application Details',
                            component: () => import('pages/application/ApplicationDetails.vue'),
                            props: (route) => ({
                              id: Number(route.params.id),
                            }),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'applicant-profile',
        component: () => import('src/layouts/applicant/ProfileLayout.vue'),
        meta: { isAuthenticated: true, isApplicant: true, profileRequired: true },
        children: applicantChildrenRoutes(),
      },
      { path: ':catchAll(.*)*', name: 'Error', component: () => import('pages/ErrorPage.vue') },
      { path: 'error', name: 'Error Page', component: () => import('pages/ErrorPage.vue') },
    ],
  },
  {
    path: '/',
    redirect: () => {
      return '/en'
    },
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    name: 'LangError',
    redirect: (to) => {
      let cleanPath = to.fullPath
      if (cleanPath.startsWith('/en-US')) cleanPath = cleanPath.replace('/en-US', '')
      if (cleanPath.startsWith('/el-GR')) cleanPath = cleanPath.replace('/el-GR', '')

      return '/en' + (cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`)
    },
  },
]

export default routes
